/**
 * ItemDisburseModal.tsx
 *
 * Port of legacy item-details.php:
 *  - Create mode (transactionId === 'NEW'): editable Category/Item/Quantity/Notes, Save.
 *  - View mode (numeric transactionId): all fields disabled, no Save.
 *
 * Item dropdown lists active+isInventory quote_item_costs with
 * "Name (X remaining)" labels; Category dropdown filters the Item list.
 * Save validates qty > 0 and qty <= selected item's quantityRemaining
 * (matches the three legacy alert() guards) then POSTs a disbursement.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Select, Spinner, Textarea } from '@/components/ui';
import {
  createInventoryTransaction,
  getInventoryTransaction,
  listDisbursableItems,
  listInventoryCategories,
} from '@/services/patient-inventory.service';

interface ItemDisburseModalProps {
  patientId: number;
  transactionId: number | 'NEW';
  onClose: () => void;
}

export function ItemDisburseModal({ patientId, transactionId, onClose }: ItemDisburseModalProps) {
  const isCreate = transactionId === 'NEW';
  return isCreate ? (
    <CreateMode patientId={patientId} onClose={onClose} />
  ) : (
    <ViewMode patientId={patientId} transactionId={transactionId} onClose={onClose} />
  );
}

// ─── Create mode ──────────────────────────────────────────────────────────────

function CreateMode({ patientId, onClose }: { patientId: number; onClose: () => void }) {
  const queryClient = useQueryClient();

  const categoriesQ = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: listInventoryCategories,
  });
  const itemsQ = useQuery({
    queryKey: ['inventory-disbursable-items'],
    queryFn: listDisbursableItems,
  });

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [itemId, setItemId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const itemsAll = itemsQ.data ?? [];

  // Only categories that actually hold a disbursable item. The endpoint lists
  // every active category on the account, and offering one with nothing behind
  // it just empties the Item dropdown with no way to tell why.
  const categoryOptions = useMemo(() => {
    const stocked = new Set(
      itemsAll.map((i) => i.categoryId).filter((id): id is number => id !== null),
    );
    return (categoriesQ.data ?? [])
      .filter((c) => stocked.has(c.quoteItemCostsCategoryID))
      .map((c) => ({
        label: c.title,
        value: c.quoteItemCostsCategoryID,
      }));
  }, [categoriesQ.data, itemsAll]);

  const filteredItems = useMemo(
    () => (categoryId ? itemsAll.filter((i) => i.categoryId === categoryId) : itemsAll),
    [itemsAll, categoryId],
  );
  const itemOptions = useMemo(
    () =>
      filteredItems.map((i) => ({
        label: `${i.name} (${i.quantityRemaining} remaining)`,
        value: i.itemId,
      })),
    [filteredItems],
  );

  const selectedItem = useMemo(
    () => itemsAll.find((i) => i.itemId === itemId) ?? null,
    [itemsAll, itemId],
  );

  const saveMut = useMutation({
    mutationFn: () =>
      createInventoryTransaction(patientId, {
        quoteItemCostId: itemId as number,
        quantityDisbursed: Number(quantity),
        inventoryNotes: notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-inventory-transactions', patientId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-disbursable-items'] });
      onClose();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Failed to save disbursement.';
      setError(msg);
    },
  });

  function handleSave() {
    setError(null);
    if (!itemId) {
      setError('Please select an item.');
      return;
    }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Please enter a quantity.');
      return;
    }
    if (selectedItem && qty > selectedItem.quantityRemaining) {
      setError('Amount disbursed cannot exceed remaining inventory.');
      return;
    }
    saveMut.mutate();
  }

  const loading = categoriesQ.isLoading || itemsQ.isLoading;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Item Disbursement"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saveMut.isPending}>
            Save
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Select
              label="Category"
              placeholder="Select one..."
              options={categoryOptions}
              value={categoryId}
              onChange={(v) => {
                setCategoryId(v);
                setItemId(null);
              }}
              clearable
            />
            <Select
              label="Item"
              placeholder="Select one..."
              options={itemOptions}
              value={itemId}
              onChange={(v) => setItemId(v)}
              searchable
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Quantity Disbursed
              </label>
              <input
                type="number"
                min="0"
                step="1"
                className="form-input-base w-full"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
          </div>

          <Textarea
            label="Notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── View mode ────────────────────────────────────────────────────────────────

function ViewMode({
  patientId,
  transactionId,
  onClose,
}: {
  patientId: number;
  transactionId: number;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-inventory-transaction', patientId, transactionId],
    queryFn: () => getInventoryTransaction(patientId, transactionId),
  });

  return (
    <Modal isOpen onClose={onClose} title="Item Disbursement" size="lg">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <p className="py-4 text-sm text-red-600">Failed to load transaction.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ReadOnlyField label="Category" value={data.categoryName ?? '—'} />
            <ReadOnlyField
              label="Item"
              value={`${data.itemName} (${data.itemQuantityRemaining} remaining)`}
            />
            <ReadOnlyField label="Quantity Disbursed" value={String(data.quantityDisbursed)} />
            <ReadOnlyField
              label="Date"
              value={data.date ? new Date(data.date).toLocaleString() : '—'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes</label>
            <textarea
              disabled
              className="form-input-base w-full resize-none bg-slate-50 text-slate-600"
              rows={4}
              value={data.notes ?? ''}
              readOnly
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <input
        className="form-input-base w-full bg-slate-50 text-slate-600"
        value={value}
        disabled
        readOnly
      />
    </div>
  );
}
